/**
 * Patient FAQ — single source of truth for both the visible accordion on the
 * Contact page and the FAQPage JSON-LD emitted by SEO.jsx.
 *
 * Google requires FAQPage structured data to mirror content that is actually
 * visible to users, so these two must never drift apart.
 */

export const FAQ_ITEMS = [
  {
    id: "hours",
    q: {
      et: "Mis kell te lahti olete?",
      ru: "Каковы часы работы?",
      en: "What are the opening hours?",
    },
    a: {
      et: "Tallinnas E–R 10–18 (Tartu mnt 16). Pärnus L 9–17 arst, T ja R 9–17 õed (Metsa 28).",
      ru: "В Таллинне Пн–Пт 10–18 (Тарту мнт 16). В Пярну Сб 9–17 врач, Вт и Пт 9–17 медсёстры (Метса 28).",
      en: "Tallinn Mon–Fri 10–18 (Tartu mnt 16). Pärnu Sat 9–17 doctor, Tue & Fri 9–17 nurses (Metsa 28).",
    },
  },
  {
    id: "booking",
    q: {
      et: "Kuidas broneerida aega?",
      ru: "Как записаться?",
      en: "How to book?",
    },
    a: {
      et: "Aega saab broneerida telefoni teel (+372 5566 5451 / +372 5566 5135), e-maili teel või digiregistratuuri kaudu.",
      ru: "Записаться можно по телефону (+372 5566 5451 / +372 5566 5135), по e-mail или через цифровую регистратуру.",
      en: "You can book by phone (+372 5566 5451 / +372 5566 5135), by email, or via online registration.",
    },
  },
  {
    id: "price",
    q: {
      et: "Kas pean maksma vastuvõtu eest?",
      ru: "Платный ли приём?",
      en: "Is the appointment paid?",
    },
    a: {
      et: "Saatekirja ja Tervisekassa kindlustusega vastuvõtt 20€ (5€ kordusvastuvõtt). Ilma saatekirjata tasuline arsti vastuvõtt alates 45€, õe iseseisev vastuvõtt alates 40€.",
      ru: "С направлением и медстраховкой приём 20€ (5€ повторный). Без направления платный приём врача от 45€, самостоятельный приём медсестры от 40€.",
      en: "With a referral and health insurance the appointment is 20€ (5€ follow-up). Without a referral, a paid doctor's appointment starts from 45€ and an independent nurse appointment from 40€.",
    },
  },
  {
    id: "referral",
    q: {
      et: "Kas ma vajan saatekirja?",
      ru: "Нужно ли мне направление?",
      en: "Do I need a referral?",
    },
    a: {
      et: "Jah, kui soovitakse tulla vastuvõtule Tervisekassa kindlustusega.",
      ru: "Да, если вы хотите прийти на приём по страховке Кассы здоровья.",
      en: "Yes, if you want to attend the appointment under Health Insurance Fund coverage.",
    },
  },
  {
    id: "min-age",
    q: {
      et: "Kas lapsi võtate vastu?",
      ru: "Принимаете ли вы детей?",
      en: "Do you see children?",
    },
    a: {
      et: "Minimaalne vanus on 16 eluaastat.",
      ru: "Минимальный возраст — 16 лет.",
      en: "The minimum age is 16 years.",
    },
  },
  {
    id: "cancellation",
    q: {
      et: "Mis on tühistamise reeglid?",
      ru: "Каковы правила отмены?",
      en: "What is the cancellation policy?",
    },
    a: {
      et: "Vastuvõtt tuleb tühistada 24 tundi enne tulekut.",
      ru: "Приём необходимо отменить за 24 часа до визита.",
      en: "An appointment must be cancelled 24 hours before the visit.",
    },
  },
  {
    id: "ultrasound-booking",
    q: {
      et: "Kas kilpnäärme ultraheli tuleb eraldi broneerida?",
      ru: "Нужно ли записываться на УЗИ щитовидной железы отдельно?",
      en: "Does thyroid ultrasound need a separate booking?",
    },
    a: {
      et: "Ei ole vaja panna eraldi aega ultraheliks.",
      ru: "Отдельное время для УЗИ записывать не нужно.",
      en: "No separate appointment for the ultrasound is needed.",
    },
  },
  {
    id: "reception",
    q: {
      et: "Kas teil on kohapeal registratuur?",
      ru: "Есть ли у вас регистратура на месте?",
      en: "Do you have an on-site reception?",
    },
    a: {
      et: "Ei ole. Aega saab broneerida telefoni teel, e-maili teel või digiregistratuuri kaudu.",
      ru: "Нет. Записаться можно по телефону, по e-mail или через цифровую регистратуру.",
      en: "No. You can book by phone, by email, or via online registration.",
    },
  },
  {
    id: "payment",
    q: {
      et: "Kuidas toimub maksmine?",
      ru: "Как происходит оплата?",
      en: "How does payment work?",
    },
    a: {
      et: "Maksta saab sularahas või kaardiga kohapeal.",
      ru: "Оплата наличными или картой на месте.",
      en: "You can pay by cash or card on-site.",
    },
  },
  {
    id: "tests-location",
    q: {
      et: "Kus tehakse uuringuid?",
      ru: "Где проводятся исследования?",
      en: "Where are investigations performed?",
    },
    a: {
      et: "Kilpnäärme ultraheli koos biopsiaga või ilma biopsiata, ABI-indeksi määramine ja silmapõhjade pildistamine tehakse kohapeal vastuvõtu ajal.",
      ru: "УЗИ щитовидной железы с биопсией или без, измерение ABI-индекса и фото глазного дна проводятся на месте во время приёма.",
      en: "Thyroid ultrasound with or without biopsy, ABI index measurement and retinal photography are performed on-site during the appointment.",
    },
  },
];

export const FAQ_COPY = {
  et: { tag: "Korduma kippuvad küsimused", title: "Sagedased küsimused" },
  ru: { tag: "Часто задаваемые вопросы", title: "Частые вопросы" },
  en: { tag: "Frequently asked questions", title: "Common questions" },
};
